import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  TrendingUp, DollarSign, Target, Star, Clock, 
  ArrowRight, CheckCircle, X, Pause
} from 'lucide-react';
import { useEnhancedCredits } from '@/hooks/useEnhancedCredits';
import { formatDistanceToNow } from 'date-fns';

export const ConversionOpportunities: React.FC = () => {
  const { 
    conversionOpportunities, 
    isLoadingOpportunities,
    respondToOpportunity
  } = useEnhancedCredits();

  if (isLoadingOpportunities) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Growth Opportunities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-32 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!conversionOpportunities || !Array.isArray(conversionOpportunities) || conversionOpportunities.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Growth Opportunities</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">No opportunities available</p>
            <p className="text-sm">Continue using the platform to unlock growth suggestions</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getOpportunityIcon = (type: string) => {
    switch (type) {
      case 'plan_upgrade':
        return <TrendingUp className="h-5 w-5 text-primary" />;
      case 'feature_unlock':
        return <Star className="h-5 w-5 text-warning" />;
      case 'bulk_discount':
        return <DollarSign className="h-5 w-5 text-success" />;
      case 'enterprise_trial':
        return <Target className="h-5 w-5 text-primary" />;
      default:
        return <TrendingUp className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getUrgencyColor = (score: number) => {
    if (score >= 80) return 'text-destructive';
    if (score >= 60) return 'text-warning';
    if (score >= 40) return 'text-primary';
    return 'text-muted-foreground';
  };

  const handleResponse = async (opportunityId: string, response: 'interested' | 'dismissed' | 'converted' | 'postponed') => {
    try {
      await respondToOpportunity.mutateAsync({ opportunityId, response });
    } catch (error) {
      console.error('Failed to respond to opportunity:', error);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Target className="h-5 w-5" />
          <span>Growth Opportunities</span>
          <Badge variant="secondary" className="ml-auto">
            {Array.isArray(conversionOpportunities) ? conversionOpportunities.length : 0}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {Array.isArray(conversionOpportunities) && conversionOpportunities.map((opportunity) => (
            <div key={opportunity.id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  {getOpportunityIcon(opportunity.opportunity_type)}
                  <div>
                    <h4 className="font-semibold text-foreground capitalize">
                      {opportunity.opportunity_type.replace('_', ' ')}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Based on your usage patterns
                    </p>
                  </div>
                </div>
                <Badge 
                  variant="outline" 
                  className={getUrgencyColor(opportunity.urgency_score || 0)}
                >
                  {Math.round(opportunity.urgency_score || 0)}% urgent
                </Badge>
              </div>

              {/* Usage Progress */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Usage Threshold</span>
                  <span className="text-sm text-muted-foreground">
                    {opportunity.current_usage || 0} / {opportunity.trigger_usage_threshold || 100}
                  </span>
                </div>
                <Progress 
                  value={opportunity.current_usage && opportunity.trigger_usage_threshold 
                    ? (opportunity.current_usage / opportunity.trigger_usage_threshold) * 100 
                    : 0} 
                  className="w-full"
                />
              </div>

              {/* ROI and Savings */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-3 bg-success/10 rounded">
                  <DollarSign className="h-5 w-5 mx-auto mb-1 text-success" />
                  <p className="text-sm font-medium text-success">Potential Savings</p>
                  <p className="text-lg font-bold text-success">
                    ${opportunity.potential_savings || 0}/mo
                  </p>
                </div>
                <div className="text-center p-3 bg-primary/10 rounded">
                  <TrendingUp className="h-5 w-5 mx-auto mb-1 text-primary" />
                  <p className="text-sm font-medium text-primary">ROI Estimate</p>
                  <p className="text-lg font-bold text-primary">
                    {opportunity.roi_estimate || 0}%
                  </p>
                </div>
              </div>

              {/* Presentation Data */}
              {opportunity.presentation_data && (
                <div className="mb-4 p-3 bg-accent rounded">
                  <h5 className="font-medium text-accent-foreground mb-2">
                    {opportunity.presentation_data.title || 'Recommendation'}
                  </h5>
                  <p className="text-sm text-accent-foreground/80">
                    {opportunity.presentation_data.description || 
                     'Upgrade to unlock additional features and better value'}
                  </p>
                  {opportunity.presentation_data.features && (
                    <ul className="text-sm text-accent-foreground/80 mt-2">
                      {opportunity.presentation_data.features.map((feature: string, i: number) => (
                        <li key={i} className="flex items-center space-x-2">
                          <CheckCircle className="h-3 w-3 text-success" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(opportunity.created_at), { addSuffix: true })}
                  </span>
                </div>

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResponse(opportunity.id, 'postponed')}
                    disabled={respondToOpportunity.isPending}
                  >
                    <Pause className="h-4 w-4 mr-1" />
                    Later
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResponse(opportunity.id, 'dismissed')}
                    disabled={respondToOpportunity.isPending}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleResponse(opportunity.id, 'interested')}
                    disabled={respondToOpportunity.isPending}
                  >
                    <ArrowRight className="h-4 w-4 mr-1" />
                    Learn More
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};